import { isNumber, isUndefined, omit, omitBy, pick } from 'lodash-es'
import type { DeepReadonly } from 'vue'
import type { SearchApiParams } from '~/api/search-api.types'
import type {
  SearchState,
  SearchAppUrlParams,
  SearchURLParams,
  StarRatingValue,
  SearchBarState,
} from '~/composables/search.types'
import {
  getDestinationFacetByCode,
  pickSearchFilterConfs,
} from '~/composables/search.conf'
import type {
  SuggestSuggestionV22,
  SuggestLocalizedDestination,
} from '~/types/openapi/search-api'

function searchParamsKeysToObject<
  K extends keyof SearchApiParams | keyof SearchURLParams,
>(
  keys: (K | null | undefined)[] | null | undefined,
): Record<
  typeof keys extends (infer R | null | undefined)[] | null | undefined
    ? R
    : never,
  true
> {
  return Object.fromEntries((keys || []).filter(Boolean).map((k) => [k, true]))
}

export function mapSearchURLParamsToDatesState(
  params: Pick<
    SearchAppUrlParams,
    'checkin' | 'checkout' | 'duration' | 'fuzzyness'
  > = {},
): DatePeriodValue | undefined {
  const { checkin, checkout, duration, fuzzyness } = params
  const startDate = tryParseDateParam(checkin)
  const endDate =
    tryParseDateParam(checkout) ??
    (startDate &&
      calculateCheckoutDate(startDate, duration ?? DURATION_DEFAULT))
  const type =
    checkout && duration && !isNumber(fuzzyness) ? 'flexible' : 'exact'
  return type === 'flexible'
    ? { type, startDate, endDate, duration }
    : { type, startDate, endDate, flexDays: fuzzyness || 0 }
}

export function mapDatePeriodToDatesState(
  period: Partial<DatePeriod> | undefined,
): DatePeriodValue | undefined {
  const { startDate, endDate } = period || {}

  if (!startDate || !endDate) return

  return omitBy(
    {
      type: isFlexDatePeriod(period) ? 'flexible' : 'exact',
      startDate,
      endDate,
      flexDays: isFuzzyDatePeriod(period) ? period.fuzziness : undefined,
      duration: isFlexDatePeriod(period) ? period.duration : undefined,
    } satisfies DatePeriodValue,
    isUndefined,
  ) as DatePeriodValue
}

export function mapDatesStateToDatePeriod(
  state: DatePeriodValue | undefined,
): DatePeriod | undefined {
  if (!state) return undefined

  const { type, startDate, endDate, flexDays, duration } = state

  if (!startDate || !endDate) return

  if (type === 'flexible') {
    return {
      startDate,
      endDate,
      duration,
    }
  }

  return { startDate, endDate, fuzziness: flexDays }
}

export function mapSearchDatesStateToURLParams(
  state: DatePeriodValue | undefined,
): Pick<SearchURLParams, 'checkin' | 'checkout' | 'duration' | 'fuzzyness'> {
  return removeEmpty({
    checkin: state?.startDate ? toDate(state.startDate) : undefined,
    checkout: state?.endDate ? toDate(state.endDate) : undefined,
    fuzzyness: state?.type === 'exact' ? state.flexDays : undefined,
    duration: state?.type === 'flexible' ? state.duration : undefined,
  })
}

export function mapSearchPaxStateToURLParams(
  pax?: number,
): Pick<SearchURLParams, 'pax'> {
  return removeEmpty({
    pax: pax === PAX_MIN ? undefined : pax,
  })
}

function mapDatesStateToSearchApiParams(
  state: DatePeriodValue | undefined | null,
): Pick<SearchApiParams, 'checkin' | 'checkout' | 'duration'> {
  if (!state) return {}
  const { startDate, endDate } = state
  const checkin = startDate ? toDate(startDate) : undefined
  const checkout = endDate ? toDate(endDate) : undefined
  if (state.type === 'exact') {
    const { flexDays = 0 } = state
    return {
      checkin: checkin && toDate(daysSub(checkin, flexDays)),
      checkout: checkout && toDate(daysAdd(checkout, flexDays)),
      duration:
        checkin && checkout ? calculateDuration(checkin, checkout) : undefined,
    }
  }
  return { checkin, checkout, duration: state.duration }
}

function petsToSearchApiParams(
  pets: boolean | undefined,
  minpets: number | undefined,
): Pick<SearchApiParams, 'pets' | 'minpets'> {
  return {
    pets: !!pets,
    minpets:
      pets === false
        ? undefined
        : Number.isFinite(minpets)
          ? tryParseInt(minpets)
          : pets === true
            ? PETS_MIN_ALLOWED
            : undefined,
  }
}

export function mapFreeweekCategoriesToSearchApiParams<T extends string>(
  categories?: T[],
): Pick<SearchApiParams, 'freeweek' | 'category'> {
  const values = categories?.map((name) => name.at(-1)!.toUpperCase()) ?? []

  return { category: values.length ? values : undefined }
}

export function mapSearchStateToSearchApiParams(
  state: Partial<SearchState>,
): Omit<SearchApiParams, 'facet' | 'pagesize'> {
  return removeFalsy({
    currency: state.currency,
    season: state.season,
    brand: state.brand,
    salesoffice: state.salesoffice,

    country: state.destination?.country,
    region: state.destination?.region,
    place: state.destination?.place,
    code: state.code, // ?
    geo: state.geo, // ?

    // Dates/Period
    ...mapDatesStateToSearchApiParams(state.dates),

    // price
    'min-price': state.priceRange?.[0],
    maxPrice: state.priceRange?.[1],

    // Map
    n: state.area?.[0],
    w: state.area?.[1],
    s: state.area?.[2],
    e: state.area?.[3],
    zoom: state.zoom,
    lat: state.coordinates?.[0],
    lon: state.coordinates?.[1],
    radius: state.radius,

    // distances
    sea: state.sea,
    lake: state.lake,
    ski: state.ski,
    center: state.center,
    'public-transport': state.publicTransport,

    pax: state.pax,
    ...petsToSearchApiParams(state.pets, state.minpets),

    accomtype: state.accommodationType,
    proptype: state.propertyType,

    ...searchParamsKeysToObject(state.features),
    ...searchParamsKeysToObject([state.pool]),
    ...searchParamsKeysToObject([state.offers]),
    ...searchParamsKeysToObject([state.holidayType]),
    ...searchParamsKeysToObject(state.special),
    ...searchParamsKeysToObject(state.fishing),
    ...searchParamsKeysToObject(state.activities),

    stars: state.starRating,
    rating: state.rating,
    reviews: state.reviews,
    bedrooms: state.bedrooms,
    bathrooms: state.bathrooms,

    discount: state.discount, // ??

    lso: state.lso,
    bo: state.bo,
    rooms: state.rooms,

    // search conf
    page: state.page,
    pagesize: state.pagesize,
    sorting: state.sorting,

    // freeweek
    ...mapFreeweekCategoriesToSearchApiParams(state.freeweekCategory),

    // ignore
    q: state.q,
  })
}

function enumerationParamsToMultiSelect<
  Name extends keyof SearchURLParams,
  Names extends Name[] = Name[],
>(names: Names, params: SearchURLParams) {
  return names.reduce((list, name) => {
    return params[name] ? [...list, name] : list
  }, [] as unknown[]) as (typeof names)[number][]
}

function enumerationParamsToSingleSelect<
  Name extends keyof SearchURLParams,
  Names extends Name[] = Name[],
>(names: Names, params: SearchURLParams) {
  return names.find((name) => name in params && params[name]) as Names[number]
}

export function mapSearchStateToURLParams(
  state: Partial<SearchState>,
): SearchURLParams {
  return {
    ...mapSearchStateToSearchApiParams(
      omit(state, [
        'accommodationType',
        'propertyType',
        'dates',
        'freeweekCategory',
        'pax',
        'publicTransport',
      ]),
    ),
    ...searchParamsKeysToObject([state.accommodationType]),
    ...searchParamsKeysToObject(state.propertyType),
    ...mapSearchDatesStateToURLParams(state.dates),
    ...mapSearchPaxStateToURLParams(state.pax),
    ...searchParamsKeysToObject(state.freeweekCategory),
    'public-transport': state.publicTransport || undefined,
    sorting: state.sorting,
  } satisfies SearchURLParams
}

export function mapSearchURLParamsToSearchState(
  params: SearchURLParams & { pagesize?: string },
): SearchState {
  const lat = tryParseFloat(params.lat)
  const lon = tryParseFloat(params.lon)

  return {
    destination: removeFalsy({
      country: params.country,
      region: params.region,
      place: params.place,
    }),
    dates: mapSearchURLParamsToDatesState(params),
    accommodationType: enumerationParamsToSingleSelect(
      ['house', 'apartment'],
      params,
    ),
    propertyType: enumerationParamsToMultiSelect(
      ['villa', 'chalet', 'farmhouse', 'studio', 'detached'],
      params,
    ),
    features: enumerationParamsToMultiSelect(
      [
        'pets',
        'wlan',
        'aircondition',
        'parking',
        'garage',
        'balcony-or-terrace',
        'dishwasher',
        'washingmachine',
        'tv',
        'sea_or_lake_view',
        'bbq',
        'boat',
        'cots',
        'hottub',
        'fireplace',
        'sauna',
        'wheelchair',
        'charging_station',
      ],
      params,
    ),
    offers: enumerationParamsToSingleSelect(
      [
        'special_offer',
        'last_minute',
        'early_booker',
        'discount-20',
        'cheapcheap',
      ],
      params,
    ),
    pool: enumerationParamsToSingleSelect(
      ['pool', 'pool_indoor', 'pool_children', 'pool_private'],
      params,
    ),

    // pax/pets
    pax: tryParseInt(params.pax),
    pets: tryParseBoolean(params.pets),
    minpets: normalizeInt(params.minpets, PETS_MIN, PETS_MAX),

    q: params.q, // TODO Remove? Legacy?

    code: params.code, // ?
    geo: params.geo, // ?

    // map
    area:
      params.n && params.w && params.s && params.e
        ? [params.n, params.w, params.s, params.e]
        : undefined,
    coordinates:
      lat && lon
        ? ([
            tryParseFloat(params.lat),
            tryParseFloat(params.lon),
          ] as CoordinatesValue)
        : undefined,
    zoom: tryParseInt(params.zoom),
    radius: params.radius,

    priceRange: [
      tryParseInt(params['min-price']) ?? 0,
      tryParseInt(params.maxPrice) ?? 0,
    ],

    // numeric values
    starRating: tryParseInt(params.stars) as StarRatingValue,
    rating: tryParseInt(params.rating),
    reviews: tryParseInt(params.reviews),
    bedrooms: tryParseInt(params.bedrooms),
    bathrooms: tryParseInt(params.bathrooms),

    // distances
    sea: tryParseInt(params.sea),
    lake: tryParseInt(params.lake),
    ski: tryParseInt(params.ski),
    center: tryParseInt(params.center),
    publicTransport: tryParseInt(params['public-transport']),

    // Holiday Type
    holidayType: enumerationParamsToSingleSelect(
      ['familyfriendly', 'holiday_resort', 'residence', 'citytrips'],
      params,
    ),

    // Something special
    special: enumerationParamsToMultiSelect(
      ['swiss_peak', 'casa', 'workation', 'sustainable'],
      params,
    ),

    // Fishing
    fishing: enumerationParamsToMultiSelect(
      ['fishing', 'fishing_certified'],
      params,
    ),

    // Activities in resort
    activities: enumerationParamsToMultiSelect(
      [
        'skiing',
        'hiking',
        'golfing',
        'cycling',
        'wellness',
        'tennis',
        'surfing',
        'sailing',
        'mountainbiking',
        'riding',
        'crosscountryskiing',
      ],
      params,
    ),

    // ???
    lso: params.lso,
    discount: params.discount,
    bo: params.bo,
    rooms: params.rooms,
    salesoffice: params.salesoffice,

    // some sort of confs?
    currency: params.currency,
    season: params.season,
    brand: params.brand,

    // configuration
    page: tryParseInt(params.page),
    pagesize: tryParseInt(params.pagesize),
    sorting: tryParseEnum(params.sorting, SORT_OPTIONS),

    // freeweek
    freeweekCategory: enumerationParamsToMultiSelect(
      ['freeweekCategoryA', 'freeweekCategoryB', 'freeweekCategoryC'],
      params,
    ),
  } satisfies SearchState
}

export function buildSearchUrl(
  filters: SearchURLParams,
  {
    destinationSlug = '',
    prefix,
    defaultPath,
    filtersConf,
  }: {
    destinationSlug?: string
    prefix?: string
    defaultPath: string
    filtersConf: SearchFilterConf[]
  },
): URL {
  const { country: countryCode, region: regionCode, place: placeCode } = filters
  const [countryDefinedSlug, regionDefinedSlug, placeDefinedSlug] =
    destinationSlug.split('/').filter(Boolean)
  const countrySlug =
    countryDefinedSlug ||
    (countryCode && getDestinationFacetByCode(countryCode)?.slug)
  const regionSlug =
    regionDefinedSlug ||
    (regionCode && getDestinationFacetByCode(regionCode)?.slug)
  const placeSlug =
    placeDefinedSlug ||
    (placeCode && getDestinationFacetByCode(placeCode)?.slug)

  if (
    (countryCode && !countrySlug) ||
    (regionCode && !regionSlug) ||
    (placeCode && !placeSlug)
  ) {
    throw new Error(
      `Missing required slugs for destination filters, country=(${countryCode}, ${countrySlug}), region=(${regionCode}, ${regionSlug}), place=(${placeCode}, ${placeSlug})`,
    )
  }

  const maxPathLength = placeSlug || regionSlug ? 3 : 2

  const confs = filtersConf as (Omit<SearchFilterConf, 'filter'> & {
    filter: keyof SearchURLParams
  })[]

  const slugs = new Map(
    [
      countrySlug && ['country', countrySlug],
      regionSlug && ['region', regionSlug],
      placeSlug && ['place', placeSlug],
      ...pickSearchFilterConfs(confs, filters)
        .filter(({ slug }) => slug.path)
        .map(({ filter, slug }) => [filter, slug.path]),
    ]
      .filter(Boolean)
      .slice(0, maxPathLength) as [string, string][],
  )

  const path = slugs.size
    ? buildPath(prefix, ...slugs.values())
    : buildPath(prefix, defaultPath)

  const query = Object.fromEntries(
    Object.entries(filters).filter(([key]) => !slugs.has(key)),
  )

  const url = useRequestURL() // safe here as it does not use nuxtApp ctx on client
  url.pathname = path
  url.search = new URLSearchParams(asQueryParams(unpack(query))).toString()
  return url
}

export function mapSearchBarStateToSearchState(
  state: DeepReadonly<SearchBarState>,
): Partial<SearchState> {
  return {
    dates: mapDatePeriodToDatesState(state.period),
    pax: state.guests.pax,
    pets: state.guests.pets ? Boolean(state.guests.pets) : undefined,
    minpets: state.guests.pets,
  }
}

/**
 * @see https://jira.migros.net/browse/IHGWEBCC-2388
 */
export function mapSuggestionToDestinationFacets(
  suggestion: SearchApiSuggestion,
): DestinationFacet[] {
  const { type, country, region, code, label, slug } = suggestion

  const slugSegments = slug.split('/').filter(Boolean)
  const facets: DestinationFacet[] = []

  // Add country facet if this is not already a country and has country data
  if (type !== 'C' && country && slugSegments[0]) {
    facets.push({
      type: 'C',
      code: country,
      slug: slugSegments[0],
    })
  }

  // Add region facet if this is a place and has region data
  if (type === 'P' && region && slugSegments[1]) {
    facets.push({
      type: 'R',
      code: region,
      slug: slugSegments[1],
    })
  }

  facets.push({
    type,
    code,
    name: label,
    slug: slugSegments[slugSegments.length - 1],
  })

  return facets
}

export function mapLegacyParamsToSearchApiParams(
  params: Params,
): SearchApiParams {
  return removeEmpty({
    ...mapSearchApiParams(params),
    ...mapFreeweekCategoriesToSearchApiParams(
      Object.keys(removeFalsy(pick(params, Object.keys(FREEWEEK_PARAMS)))),
    ),
  })
}

/**
 * Creates a {@link DestinationFacet} from {@link SuggestLocalizedDestination}.
 */
function createDestinationFacet(
  type: DestinationFacet['type'],
  destination: SuggestLocalizedDestination,
): DestinationFacet | undefined {
  const { code } = destination
  const index = type === 'C' ? 0 : type === 'R' ? 1 : -1
  const slug = destination.slug.split('/').filter(Boolean)[index]

  return slug ? { type, code, slug } : undefined
}

/**
 * @see https://jira.migros.net/browse/IHGWEBCC-2388
 */
export function mapRedesignSuggestionToDestinationFacets(
  suggestion: SuggestSuggestionV22,
): DestinationFacet[] {
  const { type, country, region, code, label } = suggestion
  const slug = suggestion.slug?.split('/').filter(Boolean).at(-1)

  return [
    // Add country facet if this is not already a country and has country data
    type !== 'C' && country && createDestinationFacet('C', country),
    // Add region facet if this is a place and has region data
    type === 'P' && region && createDestinationFacet('R', region),
    // Add main facet
    type && code && slug && { type, code, name: label, slug },
  ].filter((item) => !!item)
}
